import { useMemo } from "react";

import { DEFAULT_STRATEGY_DURATION, MarketExpanded, RoleView, getBorrowCapDetails } from "@bridgesplit/abf-react";
import { bsMath } from "@bridgesplit/utils";
import { Column, DynamicGrid, MEDIA, MediaWrapper, repeatElement } from "@bridgesplit/ui";
import { StrategyDuration } from "@bridgesplit/abf-sdk";

import PrincipalMarketsTable from "./PrincipalMarketsTable";
import PrincipalMarketCard from "./PrincipalMarketCard";
import { MarketStats } from "./types";
import { TableCardWrapper } from "../../common";

const QUERY = MEDIA.LG;
export default function PrincipalMarkets({ markets, view }: { markets: MarketExpanded[] | undefined; view: RoleView }) {
    const selectedDuration = DEFAULT_STRATEGY_DURATION;
    const marketStats = usePrincipalMarketStats(markets, selectedDuration);

    return (
        <Column spacing={2}>
            <MediaWrapper query={QUERY.below}>
                <DynamicGrid minImageWidth="300px">
                    {!marketStats &&
                        repeatElement(
                            <PrincipalMarketCard
                                selectedDuration={selectedDuration}
                                marketStats={undefined}
                                view={view}
                            />
                        )}
                    {marketStats?.map((market) => (
                        <PrincipalMarketCard
                            key={market.metadata.mint}
                            selectedDuration={selectedDuration}
                            marketStats={market}
                            view={view}
                        />
                    ))}
                </DynamicGrid>
            </MediaWrapper>
            <MediaWrapper query={QUERY.above}>
                <TableCardWrapper>
                    <PrincipalMarketsTable view={view} marketStats={marketStats} />
                </TableCardWrapper>
            </MediaWrapper>
        </Column>
    );
}

export function usePrincipalMarketStats(
    markets: MarketExpanded[] | undefined,
    _selectedDuration: StrategyDuration
): MarketStats[] | undefined {
    return useMemo(() => {
        if (!markets) return undefined;

        return markets.map(
            ({ principalStats, strategyStats, metadata, usdPrice, yieldVault, borrowCap, collateralYield }) => {
                const { principalUtilized, wAvgBorrowApy, wAvgLendApy } = principalStats;
                const { totalDeposits } = strategyStats;

                const available = totalDeposits;
                const borrowCapWarning = getBorrowCapDetails({
                    borrowCap,
                    token: metadata,
                    principalUtilized
                }).warningMessage;

                return {
                    metadata,
                    available,
                    availableUsd: bsMath.mul(available, usdPrice),
                    borrowed: principalUtilized,
                    borrowedUsd: bsMath.mul(principalUtilized, usdPrice),
                    wAvgBorrowApy: wAvgBorrowApy ?? undefined,
                    wAvgLendApy: wAvgLendApy ?? undefined,
                    yieldVault,
                    borrowCapWarning,
                    collateralYield: collateralYield === null ? undefined : collateralYield
                };
            }
        );
    }, [markets]);
}
